import React from 'react';
import { View } from 'react-native';
import moment from 'moment';

import { formatCurrency } from '../../../common/utils';
import { valueExists } from '../../../common/utils/utils';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	Card,
	SecondaryButton,
	StatusBadge,
} from '../../design/components';
import Text from '../../components/text/Text';
import TextInput from '../../components/input/TextInput';
import Switch from '../../components/switch/Switch';
import { getStatusLabel } from '../../utils/statusLabel';

import { createStocktakeDetailStyles } from './StocktakeDetail.styles';

const StocktakeDetailHeader = ({
	stocktake,
	isStocktakeCompleted,
	canEditStocktakes,
	onOpenScanner,
	onOpenManualSelect,
	onZeroUncounted,
	zeroingUncountedItems,
	uncountedItemsCount,
	searchQuery,
	onSearchChange,
	showScannedOnly,
	onShowScannedOnlyChange,
}) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const stocktakeDateValue = stocktake?.date || stocktake?.created;
	const stocktakeDateLabel = stocktakeDateValue ? moment(stocktakeDateValue).format('DD MMM YYYY') : '—';
	const stocktakeWarehouseName = stocktake?.warehouse?.name || 'Stocktake';
	const assignedUserLabel = stocktake?.user
		? (stocktake.user.destroyed
			? 'User removed'
			: (`${stocktake.user.firstname || ''} ${stocktake.user.lastname || ''}`.trim() || 'Unnamed'))
		: 'Not assigned';
	const statusLabel = getStatusLabel(stocktake?.status, 'Pending');
	const differenceLabel = valueExists(stocktake?.valueOfDifference) ? formatCurrency(stocktake.valueOfDifference) : '—';
	const varianceLabel = valueExists(stocktake?.percentageDifference) ? `${stocktake.percentageDifference}%` : '—';

	return (
		<View style={styles.header}>
			<View style={styles.titleBlock}>
				<View style={styles.titleRow}>
					<Text style={styles.headingText} accessibilityRole="header">
						{stocktakeWarehouseName}
					</Text>
					<StatusBadge
						label={statusLabel}
						tone={isStocktakeCompleted ? 'success' : 'neutral'}
						size="small"
					/>
				</View>
				<Text style={styles.subheadingText}>
					{stocktakeDateLabel}
				</Text>
				<Text style={styles.metaText}>
					Person: {assignedUserLabel}
				</Text>
			</View>
			{isStocktakeCompleted && (
				<Card tone="warning" style={styles.noticeCard}>
					<Text style={styles.warningText}>
						This stocktake has been completed and cannot be modified.
					</Text>
				</Card>
			)}
			<View style={styles.summaryRow}>
				<View style={styles.summaryTile}>
					<Text style={styles.summaryLabel}>Value difference</Text>
					<Text style={styles.summaryValue}>{differenceLabel}</Text>
				</View>
				<View style={styles.summaryTile}>
					<Text style={styles.summaryLabel}>Variance</Text>
					<Text style={styles.summaryValue}>{varianceLabel}</Text>
				</View>
			</View>
			{
				stocktake?.valueNotAccurate && (
					<Text style={styles.warningText}>
						Value may not be accurate – some products do not have costs.
					</Text>
				)
			}
			<View style={styles.buttonRow}>
				<SecondaryButton
					style={styles.halfButton}
					icon="camera"
					label="Scan product"
					onPress={onOpenScanner}
					disabled={!canEditStocktakes}
				/>
				<SecondaryButton
					style={styles.halfButton}
					icon="search"
					label="Select product"
					onPress={onOpenManualSelect}
					disabled={!canEditStocktakes}
				/>
			</View>
			<SecondaryButton
				variant="tonal"
				label="Zero uncounted items"
				onPress={onZeroUncounted}
				loading={zeroingUncountedItems}
				disabled={!canEditStocktakes || !uncountedItemsCount}
			/>
			<TextInput
				value={searchQuery}
				onChange={onSearchChange}
				placeholder="Search products or barcodes"
			/>
			<View style={styles.toggleRow}>
				<Text style={styles.toggleLabel}>Show counted only</Text>
				<Switch
					value={showScannedOnly}
					onValueChange={onShowScannedOnlyChange}
				/>
			</View>
		</View>
	);
};

export default StocktakeDetailHeader;
